/**
 * Whist — server-authoritative rules engine (classic partnership trick game, 4 players).
 *
 * Rules implemented (toggles live in {@link WhistConfig}):
 *  - 4 players in two partnerships: seats 0 & 2 (team 0) against seats 1 & 3 (team 1). One 52-card deck, ace high,
 *    13 cards each, dealt one at a time starting left of the dealer, so the dealer receives the last card.
 *  - The dealer's last card is turned face up for everyone to see; its suit is trump for the hand. The card then
 *    belongs to the dealer's hand (it is shown until the dealer plays it).
 *  - There is no bidding. The player left of the dealer leads the first trick; the trick winner leads the next.
 *  - Players must follow the led suit when able; a void player may play anything.
 *  - Trick winner: highest trump, else highest card of the led suit.
 *  - Scoring per hand: the first six tricks won by a partnership are its "book" and score nothing; the partnership
 *    that took more than six tricks scores one point per trick above six. Honours scoring is omitted.
 *  - The match ends when a partnership reaches `targetScore` and leads; a tie after both pass it plays another hand.
 *    `maxHands` is a hard cap so a match always terminates (then the higher total wins, equal totals share first).
 *    Partners share a place, so payouts are split between the two partners.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId, type Suit } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface WhistConfig {
  /** Match ends when a partnership reaches this score and leads. */
  targetScore: number;
  /** Hard cap on hands so a match always terminates. */
  maxHands: number;
  handEndDelayMs: number;
  trickEndDelayMs: number;
}

export type WhistPhase = 'PLAYING' | 'TRICK_END' | 'HAND_END' | 'MATCH_END';

export interface WhistTrickPlay {
  seat: number;
  card: CardId;
}

export interface WhistCompletedTrick {
  leader: number;
  plays: WhistTrickPlay[];
  winner: number;
}

/** Per-hand result for one partnership (public once the hand ends). */
export interface WhistTeamHand {
  /** Tricks won by both partners together. */
  tricks: number;
  /** Points scored: tricks above six, or 0. */
  points: number;
}

export interface WhistState {
  config: WhistConfig;
  players: string[];
  hand: number;
  dealer: number;
  phase: WhistPhase;
  hands: CardId[][];
  /** The dealer's last card, turned face up; its suit is trump. */
  trumpCard: CardId;
  trump: Suit;
  tricksWon: number[];
  turn: number;
  trick: WhistTrickPlay[];
  tricks: WhistCompletedTrick[];
  /** Team scores, index = team (seat % 2). */
  teamScores: number[];
  /** One row per finished hand: [team0, team1]. */
  handResults: WhistTeamHand[][];
}

export type WhistAction = { type: 'play'; card: CardId };

export type WhistLegal = { kind: 'play'; cards: CardId[] } | { kind: 'none' };

export interface WhistView {
  gameType: 'WHIST';
  config: WhistConfig;
  seat: number | null;
  phase: WhistPhase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  trumpCard: CardId;
  trump: Suit;
  /** True while the turned-up card is still in the dealer's hand. */
  trumpCardInHand: boolean;
  tricksWon: number[];
  trick: WhistTrickPlay[];
  lastTrick: WhistCompletedTrick | null;
  tricks: WhistCompletedTrick[];
  teamScores: number[];
  handResults: WhistTeamHand[][];
  /** Score of each seat's team (so generic score UIs work). */
  totals: number[];
}

const DEFAULTS: WhistConfig = {
  targetScore: 10,
  maxHands: 20,
  handEndDelayMs: 5000,
  trickEndDelayMs: 900,
};

const SEATS = 4;
const BOOK = 6;

export const whistTeamOf = (seat: number): number => seat % 2;
export const whistPartnerOf = (seat: number): number => (seat + 2) % SEATS;

export function whistTrickWinner(plays: readonly WhistTrickPlay[], trump: Suit): number {
  const lead = parseCard(plays[0]!.card).suit;
  let best = plays[0]!;
  for (const p of plays.slice(1)) {
    const c = parseCard(p.card);
    const b = parseCard(best.card);
    const beats = (c.suit === trump && b.suit !== trump) || (c.suit === b.suit && c.rank > b.rank);
    if (beats && (c.suit === trump || c.suit === lead)) best = p;
  }
  return best.seat;
}

export function whistLegalCards(hand: readonly CardId[], trick: readonly WhistTrickPlay[]): CardId[] {
  if (trick.length === 0) return [...hand];
  const lead = parseCard(trick[0]!.card).suit;
  const followers = hand.filter((c) => parseCard(c).suit === lead);
  return followers.length > 0 ? followers : [...hand];
}

/** Score one partnership's hand: one point per trick above the six-trick book. */
export function whistScoreTeamHand(tricks: number): WhistTeamHand {
  return { tricks, points: Math.max(0, tricks - BOOK) };
}

function deal(state: WhistState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(buildDeck(), rng);
  const hands: CardId[][] = [[], [], [], []];
  deck.forEach((c, i) => hands[(state.dealer + 1 + i) % SEATS]!.push(c));
  state.hands = hands.map((h) => sortHand(h));
  state.trumpCard = deck[deck.length - 1]!;
  state.trump = parseCard(state.trumpCard).suit;
  state.tricksWon = [0, 0, 0, 0];
  state.trick = [];
  state.tricks = [];
  state.phase = 'PLAYING';
  state.turn = (state.dealer + 1) % SEATS;
  events.push({ type: 'dealt', hand: state.hand, dealer: state.dealer, trumpCard: state.trumpCard, trump: state.trump, counts: state.hands.map((h) => h.length) });
}

/** Partners share a place: the leading team is 1st, the other 2nd (both 1st on a tie). */
function teamStandings(state: WhistState): Standing[] {
  const [a, b] = state.teamScores as [number, number];
  return [0, 1, 2, 3].map((seat) => {
    const team = whistTeamOf(seat);
    const mine = team === 0 ? a : b;
    const theirs = team === 0 ? b : a;
    return { seat, score: mine, place: mine >= theirs ? 1 : 2 };
  });
}

export const whistEngine: GameEngine<WhistConfig, WhistState, WhistAction, WhistView, WhistLegal> = {
  gameType: 'WHIST',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 1, 100);
    checkInt('maxHands', c.maxHands, 1, 50);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Whist needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: WhistState = {
      config,
      players,
      hand: 1,
      dealer: rng.int(SEATS),
      phase: 'PLAYING',
      hands: [],
      trumpCard: '2S',
      trump: 'S',
      tricksWon: [],
      turn: 0,
      trick: [],
      tricks: [],
      teamScores: [0, 0],
      handResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Whist action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(seat === state.turn && state.phase === 'PLAYING', 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(whistLegalCards(hand, state.trick).includes(action.card), 'ILLEGAL_MOVE', 'You must follow suit');
    removeCards(hand, [action.card]);
    state.trick.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card });
    if (state.trick.length < SEATS) {
      state.turn = (seat + 1) % SEATS;
      return events;
    }
    const winner = whistTrickWinner(state.trick, state.trump);
    state.tricksWon[winner]!++;
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length });
    state.phase = 'TRICK_END';
    state.turn = winner;
    return events;
  },

  getActors(state) {
    return state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn || state.phase !== 'PLAYING') return { kind: 'none' };
    return { kind: 'play', cards: whistLegalCards(state.hands[seat]!, state.trick) };
  },

  getTimeoutAction(state, seat) {
    const legal = whistLegalCards(state.hands[seat]!, state.trick);
    // Lowest legal card, trump last: rarely wastes a winner and is never illegal.
    const lowest = [...legal].sort((a, b) => {
      const ca = parseCard(a);
      const cb = parseCard(b);
      return (ca.suit === state.trump ? 100 : 0) + ca.rank - ((cb.suit === state.trump ? 100 : 0) + cb.rank);
    })[0]!;
    return { type: 'play', card: lowest };
  },

  getView(state, seat) {
    return {
      gameType: 'WHIST',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.hand,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      trumpCard: state.trumpCard,
      trump: state.trump,
      trumpCardInHand: state.hands[state.dealer]!.includes(state.trumpCard),
      tricksWon: [...state.tricksWon],
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      teamScores: [...state.teamScores],
      handResults: state.handResults.map((r) => r.map((x) => ({ ...x }))),
      totals: [0, 1, 2, 3].map((s) => state.teamScores[whistTeamOf(s)]!),
    };
  },

  getSystemStep(state) {
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'HAND_END') return { kind: 'nextHand', delayMs: state.config.handEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      if (state.tricks.length < 13) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.turn });
        return events;
      }
      const cfg = state.config;
      const results = [0, 1].map((team) => whistScoreTeamHand(state.tricksWon[team]! + state.tricksWon[team + 2]!));
      results.forEach((r, team) => {
        state.teamScores[team]! += r.points;
      });
      state.handResults.push(results);
      events.push({
        type: 'handEnd',
        hand: state.hand,
        results,
        teamScores: [...state.teamScores],
        tricksWon: [...state.tricksWon],
      });
      const [a, b] = state.teamScores as [number, number];
      const reached = a >= cfg.targetScore || b >= cfg.targetScore;
      const capped = state.hand >= cfg.maxHands;
      if ((reached && a !== b) || capped) {
        state.phase = 'MATCH_END';
        events.push({ type: 'matchEnd', standings: teamStandings(state) });
      } else {
        state.phase = 'HAND_END';
      }
      return events;
    }
    if (state.phase === 'HAND_END') {
      state.hand++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
      return events;
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return teamStandings(state);
  },

  getRoundsCompleted: (state) => state.handResults.length,
};
